import { z } from "zod";
//#region src/app-events.ts
/**
* App-scoped Kubernetes event wire types — `GET /v1/admin/apps/:appId/dps/:dpId/events`
* (site-admin-api observability module; R4 **E4**). *"What did Kubernetes
* complain about?"*
*
* `:appId` and `:dpId` are PATH params and are NOT part of the query schema
* (parity with `appLogsQuerySchema`, which likewise omits the path appId).
* `:appId` is the `release.apps.id` UUID — the resource-grant key, gated by
* `@RequireResourcePermission('cp:app:runtime:read','appId')`.
*
* ⛔ **THE PERMISSION IS `cp:app:runtime:read`, NOT `cp:app:logs:read`.** R4
* ruling R4-05: `design/02` §2.1 introduced `cp:app:logs:read` and explicitly
* rejected the alternative — then R2 shipped the rejected one. `cp:app:logs:read`
* has zero hits repo-wide. Do not add a 16th permission for this surface.
*
* ## What this surface REFUSES, and why the refusal is `.strict()`
*
* `design/02` §2.4 requires the app surface to refuse `namespace`,
* `labelSelector`, `fieldSelector`, `kind` and `orgId` outright, plus any query
* language (`logql`/`query`/`expr`/`promql`). All of them are absent from the
* schema below, and `.strict()` (#466) turns each into a 400 naming the key —
* enforced recursively by `ZionStrictValidationPipe`. A bespoke per-key handler
* would be more code and a weaker guarantee: `.strict()` refuses keys nobody has
* thought of yet, which is the actual threat.
*
* The two that matter most, because the DP would accept them:
*   - **`labelSelector`** — the DP takes it **unvalidated**
*     (`observability.controller.ts` `listPods`/`listResources`). There must be
*     no client path onto it.
*   - **`involvedObjectName`** → a k8s `fieldSelector`. R3 bounded it DNS-1123 at
*     the DP door, but it is still a raw object-name reach and is not something
*     an app owner may aim. The CP sets it server-side or not at all.
*
* `namespace` is likewise server-derived (resolved from `:dpId`), never sent.
*/
/**
* Hard cap on `limit`, from `design/02` §2.4 (*"Unbounded time spans / limits …
* Events ≤ 200"*).
*
* ⚠️ **This lives here because it is the CP-facing app-events bound.** R4
* ships CP-only (ruling R4-01) and touches no `cloud/dp/` source, so at the
* time R4 shipped, the DP's own `parseLimit` still accepted `limit=1000000`.
* That half of #880 is now closed too: `cloud/dp/core-api`'s
* `observability.controller.ts` imports THIS symbol directly (core-api
* already depends on `@zion/sdk-contracts` at `workspace:*`) and passes it as
* the bound for its own `/internal/observability/events` route, so both doors
* enforce the same 200 from one declaration. The DP-side `truncated` field is
* a separate, still-open follow-up.
*/
const MAX_EVENT_LIMIT = 200;
/**
* The bound the DP's INTERNAL observability door applies to its own `limit` — a DIFFERENT
* question from {@link MAX_EVENT_LIMIT}, and conflating the two produced a one-day-old
* regression in which `get_app_events` returned a hard 502 for EVERY caller.
*
* ⛔ THE TWO QUESTIONS, because one constant was answering both:
*  - {@link MAX_EVENT_LIMIT} = the CALLER-FACING PAGE SIZE. It bounds `appEventsQuerySchema`'s
*    `limit`, i.e. how many events a CP API consumer may ask to be handed.
*  - This constant = the INTERNAL UPSTREAM-FETCH bound. The CP deliberately over-fetches
*    namespace-wide events and filters them down to one app, because 200 namespace-wide events
*    may contain only ~12 for the app in question; without the headroom the caller receives 12
*    and believes they have seen everything. `app-events.service.ts` argues this in as many
*    words: the over-fetch *"is not slack — it is required for the caller's limit to mean
*    anything"*.
*
* ⚠️ HOW IT BROKE: `DP_FETCH_LIMIT = MAX_EVENT_LIMIT * 5` (=1000) shipped 2026-08-02 and worked
* for 16 days. On 2026-08-18 the internal door was hardened to `parseLimit(limit,
* MAX_EVENT_LIMIT)` — correct for a page size, wrong for this fetch — so every CP call became a
* DP 400, wrapped by provisioning-svc as a 500, surfaced by site-admin-api as a 502.
* ⛔ The tempting one-line fix (`DP_FETCH_LIMIT = 200`) trades a loud 502 for a SILENTLY SHORT
* ANSWER, which is worse. Splitting the constant keeps #992's security property (the internal
* door is still bounded) AND the headroom the CP requires.
*
* ⭐ Derived from `MAX_EVENT_LIMIT` rather than written as `1000`, so the CP's over-fetch factor
* and the DP's acceptance bound cannot drift apart: they are now one expression, in one file,
* that both sides import.
*/
const MAX_INTERNAL_EVENT_FETCH = 1e3;
/** Page size when the caller does not ask (`design/02` §2.2 E4: `[1,200] default 100`). */
const DEFAULT_EVENT_LIMIT = 100;
/** k8s event type. An allowlist, not a free string — filtered CP-side. */
const appEventTypeSchema = z.enum(["Warning", "Normal"]);
const appEventsQuerySchema = z.object({
	/**
	* Restrict to one fixture. Matched against the fixture id **parsed from
	* `involvedObject.name`** (segment 1 of `<appKey>--<fixtureId>--<versionSlug>`),
	* so it is the fixture **id/slug**, never a uuid.
	*
	* A value that matches nothing yields `events: []` — `empty`, the correct
	* rendering for "the filter matched nothing". Deliberately NOT a 400: the
	* caller asked a well-formed question that happens to have no answer, and
	* conflating that with a malformed request is the `empty`/`errorstate`
	* collapse this whole release exists to end.
	*
	* `.max(63)` is the DNS-1123 label bound a fixture id already obeys.
	*/
	fixtureId: z.string().min(1).max(63).optional(),
	/** Restrict to `Warning` or `Normal`. Applied CP-side — the DP's `ListEventsQuery` has no `type`. */
	type: appEventTypeSchema.optional(),
	/**
	* ISO-8601 lower time bound, forwarded to the DP.
	*
	* ⚠️ ISO-ONLY, deliberately, and this is NOT the wider accept-set used by the
	* CP↔DP `GetLogsQuery.start` field. That field accepts a bare integer epoch
	* because the site-admin Logs tab pages by putting a 19-digit nanosecond
	* cursor in it (R2-08). This one has no cursor and reaches the DP's
	* `assertIsoTimestamp`, which is ISO-only — so accepting an epoch here would
	* mint a value the sink rejects. Per-hop rule: a hop may narrow, so long as
	* its forward-set stays a SUBSET of the sink's accept-set.
	*/
	since: z.string().datetime().optional(),
	/**
	* Page size, `[1, MAX_EVENT_LIMIT]`, default `DEFAULT_EVENT_LIMIT`.
	* `z.coerce` because it arrives as a query string.
	*/
	limit: z.coerce.number().int().min(1).max(200).optional()
}).strict();
/** The k8s object an event is about. Kept STRUCTURED — see `appEventSchema`. */
const appEventInvolvedObjectSchema = z.object({
	kind: z.string(),
	name: z.string()
}).strict();
/**
* One Kubernetes event, attributed to a fixture/version where the object name
* permits it.
*
* ⚠️ `involvedObject` stays a STRUCTURED `{kind,name}` — do not flatten it to a
* `"Kind/name"` string the way `provisioning-svc`'s legacy `mapRestEventToLegacy`
* does. The reason is that `kind` SELECTS THE PARSE RULE (a Pod name carries two
* trailing k8s hash segments, a ReplicaSet one, a Deployment none), so it is a
* semantic field, not decoration.
*
* A flattened string is in fact splittable back — a kind contains no `/` and a
* name is a DNS-1123 subdomain — so this is a clarity and contract requirement,
* NOT the impossibility an earlier draft of this comment asserted. What the
* legacy route genuinely cannot do is express `since`/`limit`, and it collapses
* an absent timestamp to `''`.
*/
const appEventSchema = z.object({
	type: z.string(),
	reason: z.string(),
	message: z.string(),
	count: z.number().int().nonnegative(),
	firstTimestamp: z.string().nullable(),
	lastTimestamp: z.string().nullable(),
	involvedObject: appEventInvolvedObjectSchema,
	/**
	* Parsed from `involvedObject.name`; `null` when the name does not carry it.
	*
	* ⛔ **`null` IS A REAL AND EXPECTED VALUE, NOT AN ERROR.** A namespace holds
	* events about objects that are not app workloads at all, and an event that
	* cannot be attributed is still an event — it is never dropped from the
	* response. Rendering an unattributable event as absent would be the
	* `errorstate`-as-`empty` failure R4's canon forbids, one level down.
	*
	* `.nullable()` and NOT `.optional()`: the producer always decides, so the
	* key is always present and `null` is an affirmative "no attribution",
	* distinguishable from a producer that forgot to set it.
	*/
	fixtureId: z.string().nullable(),
	/** Parsed from `involvedObject.name`; `null` on the same terms as `fixtureId`. */
	versionSlug: z.string().nullable()
}).strict();
const appEventsResponseSchema = z.object({
	/** Echoes the `:appId` path param verbatim (the `release.apps.id` UUID, not the slug). */
	appId: z.string().uuid(),
	/** Echoes the `:dpId` path param verbatim. */
	dpId: z.string().uuid(),
	/** Newest first. Empty means the filters matched nothing — never an error. */
	events: z.array(appEventSchema),
	/**
	* TRUE ⇒ the caller is not seeing everything that matched.
	*
	* ⛔ **Computed EXACTLY, never as `events.length === limit`.** An exact-length
	* page is the single most common honest answer, and reporting it as truncated
	* trains callers to ignore the flag. The server compares the FULL filtered set
	* against `limit` before slicing (`filtered.length > limit`).
	*
	* It is also TRUE when the upstream fetch hit its own ceiling, because in
	* that case the CP genuinely cannot prove completeness. `truncated` means
	* *"there may be more"* — it never claims completeness it cannot back.
	*
	* `design/02` §2.4 requires this field: *"Silent truncation — every bounded
	* response carries `truncated: boolean`."*
	*/
	truncated: z.boolean()
}).strict();
//#endregion
export { DEFAULT_EVENT_LIMIT, MAX_EVENT_LIMIT, MAX_INTERNAL_EVENT_FETCH, appEventInvolvedObjectSchema, appEventSchema, appEventTypeSchema, appEventsQuerySchema, appEventsResponseSchema };

//# sourceMappingURL=app-events.js.map